/**
 * Cliente HTTP de la API.
 *
 * Con el backend en US East y los usuarios en Lima (~55–90 ms de RTT), cada
 * round-trip cuenta. De ahí dos decisiones:
 *   - Las operaciones por lotes existen en la API y hay que usarlas: pedir
 *     firma archivo a archivo para 400 fotos serían ~32 s sólo de latencia.
 *   - Una sola petición de refresh en vuelo: si 5 llamadas reciben 401 a la vez,
 *     comparten el mismo refresh en lugar de disparar cinco.
 */

export interface ApiError {
  code: string
  message: string
  details?: Record<string, string[]>
  request_id?: string
}

export class ApiException extends Error {
  constructor(
    readonly status: number,
    readonly error: ApiError,
  ) {
    super(error.message)
    this.name = 'ApiException'
  }

  get isValidation(): boolean {
    return this.error.code === 'validation_failed'
  }

  get isQuotaExceeded(): boolean {
    return this.error.code === 'quota_exceeded'
  }
}

/* Exportada para que el informador de errores del navegador apunte al MISMO
   sitio sin volver a escribir la expresión: dos definiciones de la base de la
   API acaban divergiendo, y la que se quedaría vieja sería la del informador,
   que es la que nadie mira hasta el día que hace falta. */
export const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

/*
 | Cuánto se espera antes de dar una petición por perdida.
 |
 | `fetch` NO tiene timeout propio: una petición que no recibe respuesta se
 | queda colgada para siempre. Y una promesa que nunca se resuelve no es un
 | error — es una pantalla cargando eternamente, sin mensaje y sin nada que
 | pulsar. Le pasó a la sección de precios de la portada: tres cajas grises
 | fijas, con el `ErrorState` y su botón «Reintentar» a dos líneas de distancia
 | en el mismo archivo, inalcanzables — React Query no puede contar un fallo
 | que no ocurre, así que ni reintenta ni pasa a su estado de error.
 |
 | No es un caso raro. El caso de uso principal es un móvil con datos en Perú,
 | y una conexión que se queda a medias —túnel, ascensor, cambio de celda— hace
 | exactamente esto. Es peor que un error: un error se reintenta.
 |
 | 20 s es holgado incluso con el RTT Lima↔US East de ~55–90 ms. No está
 | pensado para acelerar nada, sino para que exista un final.
 */
const TIMEOUT_MS = 20_000

/** Sentinela para distinguir nuestro reloj de un `abort()` del llamante. */
const POR_RELOJ = Symbol('timeout')

let accessToken: string | null = null
let refreshInFlight: Promise<string | null> | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

interface RequestOptions<B = unknown> extends Omit<RequestInit, 'body'> {
  body?: B
  /** Interno: evita bucle infinito si el propio refresh devuelve 401. */
  skipRefresh?: boolean
  /**
   * Cabecera Authorization explícita.
   *
   * La usa el portal del cliente final, que se autentica con `Share <jwt>` y
   * NO tiene refresh token: un 401 ahí significa "vuelve a abrir el enlace",
   * no "renueva la sesión". Por eso pasar esto desactiva el reintento.
   */
  authorization?: string

  /**
   * Qué se espera de vuelta.
   *
   * `blob` es para lo que no es JSON: hoy, el PDF del comprobante. Va como
   * opción de `apiFetch` y no como una función aparte para no duplicar lo que
   * de verdad importa aquí — la cabecera de sesión y el reintento tras renovar
   * el token. Un descargador propio se olvidaría del segundo, y el síntoma
   * sería «el PDF falla si dejas la pestaña abierta un rato».
   *
   * Y por eso mismo el PDF no puede ser un `<a href>` a secas: una navegación
   * del navegador no manda la cabecera `Authorization`.
   */
  expect?: 'json' | 'blob'

  /**
   * Milisegundos antes de abortar. `0` lo desactiva.
   *
   * Por defecto no lleva reloj lo que legítimamente tarda: la subida del logo
   * (multipart) y las descargas (`blob`). Ahí un límite fijo cancelaría una
   * transferencia que va bien, que es justo el fallo que este cliente existe
   * para no tener — el producto entero va de archivos pesados por redes malas.
   */
  timeout?: number
}

export async function apiFetch<T, B = unknown>(
  path: string,
  options: RequestOptions<B> = {},
): Promise<T> {
  const {
    body,
    skipRefresh,
    authorization,
    headers,
    expect = 'json',
    timeout,
    signal,
    ...rest
  } = options

  /*
   | `FormData` va tal cual, sin serializar y sin `Content-Type`.
   |
   | Lo usa la subida del logo, que es lo único del producto que sube por PHP
   | (el resto va directo a R2). La cabecera la tiene que poner el navegador
   | porque incluye el `boundary` que separa las partes del multipart: fijarla
   | a mano produce un cuerpo que el servidor lee vacío, y el síntoma —«el
   | campo logo es obligatorio» con un archivo adjunto— no apunta a la causa.
   */
  const isMultipart = typeof FormData !== 'undefined' && body instanceof FormData

  /*
   | El reloj.
   |
   | Se combina a mano y no con `AbortSignal.any`, que pide Safari 17.4: hay
   | bastante iPhone por debajo de eso entre los clientes finales, y son
   | exactamente los que abren la galería desde el móvil.
   */
  const limite = timeout ?? (expect === 'blob' || isMultipart ? 0 : TIMEOUT_MS)
  const aborto = new AbortController()
  const reloj = limite > 0 ? setTimeout(() => aborto.abort(POR_RELOJ), limite) : undefined

  signal?.addEventListener('abort', () => aborto.abort(signal.reason), { once: true })

  let response: Response

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      ...rest,
      signal: aborto.signal,
      // El refresh token viaja en cookie HttpOnly; sin esto no se envía.
      credentials: 'include',
      headers: {
        Accept: expect === 'blob' ? '*/*' : 'application/json',
        ...(body !== undefined && !isMultipart ? { 'Content-Type': 'application/json' } : {}),
        ...(authorization !== undefined
          ? { Authorization: authorization }
          : accessToken !== null
            ? { Authorization: `Bearer ${accessToken}` }
            : {}),
        ...headers,
      },
      body: isMultipart ? body : body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch (causa) {
    /*
     | Una cancelación del llamante se propaga tal cual: un componente que se
     | desmonta no es un fallo del servidor, y disfrazarlo de error pintaría un
     | aviso en una pantalla que el usuario ya dejó atrás.
     */
    if (aborto.signal.reason !== POR_RELOJ) throw causa

    /*
     | 504 y no 0 ni 408, a propósito. La política de `main.tsx` no reintenta
     | por debajo de 500 —«un 403 no mejora insistiendo»— y un timeout es
     | justo lo contrario: es lo que más merece otro intento.
     */
    throw new ApiException(504, {
      code: 'timeout',
      message: 'El servidor tardó demasiado en responder.',
    })
  } finally {
    clearTimeout(reloj)
  }

  if (response.status === 401 && !skipRefresh && authorization === undefined) {
    const renewed = await refreshAccessToken()

    if (renewed !== null) {
      return apiFetch<T, B>(path, { ...options, skipRefresh: true })
    }
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: ApiError } | null

    throw new ApiException(
      response.status,
      payload?.error ?? { code: 'network_error', message: 'No se pudo contactar con el servidor.' },
    )
  }

  if (response.status === 204) return undefined as T

  if (expect === 'blob') return (await response.blob()) as T

  return (await response.json()) as T
}

/** Una única petición de refresh compartida por todas las llamadas en vuelo. */
async function refreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= (async () => {
    try {
      const result = await apiFetch<{ data: { access_token: string } }>('/auth/refresh', {
        method: 'POST',
        skipRefresh: true,
      })

      setAccessToken(result.data.access_token)
      return result.data.access_token
    } catch {
      setAccessToken(null)
      return null
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/**
 * El segundo parámetro de tipo es el CUERPO que se manda.
 *
 * Por defecto `unknown`, que acepta cualquier cosa y es como estaba: un campo
 * mal escrito o un valor fuera de la lista no se descubría hasta el 422 del
 * servidor. Pasando el tipo generado —`api.post<Respuesta, CreateProjectBody>`—
 * lo dice `tsc` antes de salir el paquete.
 *
 * Se hace explícito y no obligatorio porque no todos los endpoints tienen su
 * cuerpo marcado con `#[CuerpoApi]` todavía; los que sí, deberían usarlo.
 */
export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T, B = unknown>(path: string, body?: B) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T, B = unknown>(path: string, body?: B) => apiFetch<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  /** Descarga binaria con la sesión puesta. Ver `expect` en RequestOptions. */
  blob: (path: string) => apiFetch<Blob>(path, { expect: 'blob' }),
}
